import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';

import { type OrganizationAgentLocation } from '../Agent';
import type { AgentChatCompiledPrompt } from '../AgentChat';
import type { AgentSiteSetupGuideStarter } from '../AgentSiteSetup';

/**
 * What an empty conversation knows about where it was opened, once the user has taken out of it
 * whatever they did not want in: the location the dock reports, less an excluded target, and the
 * page and selection only while they are kept in context.
 */
export type AgentDockWelcomeContext = {
    location: Pick<OrganizationAgentLocation, 'siteId' | 'spaceId' | 'changeRequest'>;
    /** Whether a page is in context. Only ever offered from a change request or a space edited live. */
    page: boolean;
    /** Whether text selected in that page is in context. */
    selection: boolean;
    /** Whether an analytics report of the site is on screen. */
    analytics: boolean;
    /**
     * The site's setup guide, started or continued while the site is being set up: null once its
     * setup is over or where it is not offered, unknown while that loads.
     */
    siteSetup?: AgentSiteSetupGuideStarter | null;
};

/**
 * A way to start the conversation, offered as a pill.
 */
export type AgentDockStarter = {
    /** Identifies the starter in analytics, as a template prompt's id does. */
    id: string;
    icon: IconDefinition;
    /** Ends with an ellipsis when the prompt is left for the user to finish, so the pill says so. */
    label: string;
    /** What the agent is asked — in full, or up to the point where the user takes over. */
    prompt: string;
    /**
     * Sent as it is. Otherwise written into the composer, for a request the user has to complete —
     * a topic, a language — before it means anything.
     */
    immediate: boolean;
};

/**
 * Where the user is, as far as the welcome is concerned: the most specific thing in context wins,
 * since it is also what an unqualified "this" means to the agent.
 */
export type AgentDockWelcomePlace =
    | 'selection'
    | 'page_in_change_request'
    | 'page_in_live_space'
    | 'change_request'
    | 'space'
    | 'analytics'
    | 'site'
    | 'site_setup'
    | 'organization';

export type AgentDockWelcome = {
    /** The place the welcome speaks for. Reported with a pick, so a starter can be read in context. */
    place: AgentDockWelcomePlace;
    title: string;
    /** What the agent can do where the user is, in a line. */
    description: string;
    starters: AgentDockStarter[];
};

const DEFAULT_TITLE = 'GitBook Agent';

const WRITE_PAGE: AgentDockStarter = {
    id: 'welcome.write-page',
    icon: Icon.Page,
    label: 'Write a new page about…',
    prompt: 'Write a new page about ',
    immediate: false,
};

const FIX_PAGE_TYPOS: AgentDockStarter = {
    id: 'welcome.page.typos',
    icon: Icon.AIFixSpellingGrammar,
    label: 'Fix typos and grammar',
    prompt: 'Fix all typos and grammar in this page',
    immediate: true,
};

const IMPROVE_PAGE: AgentDockStarter = {
    id: 'welcome.page.improve',
    icon: Icon.AIImproveWriting,
    label: 'Improve this page',
    prompt: 'Review and improve this page',
    immediate: true,
};

const TRANSLATE_PAGE: AgentDockStarter = {
    id: 'welcome.page.translate',
    icon: Icon.AITranslate,
    label: 'Translate this page into…',
    prompt: 'Translate this page into ',
    immediate: false,
};

const IMPROVE_SITE_NAVIGATION: AgentDockStarter = {
    id: 'welcome.site.structure',
    icon: Icon.SiteStructure,
    label: 'Improve site navigation',
    prompt: "Review this site's structure and suggest how to improve it",
    immediate: true,
};

/** The starter that opens the site's setup guide, rather than asking in this conversation. */
export const SITE_SETUP_GUIDE_STARTER_ID = 'welcome.site.setup.guide';

export const SITE_SETUP_GUIDE_STARTER: AgentDockStarter = {
    id: SITE_SETUP_GUIDE_STARTER_ID,
    icon: Icon.Stepper,
    label: 'Start the setup guide',
    prompt: 'Start the site setup guide',
    immediate: true,
};

/**
 * Every prompt speaks of "this site", "this page" or "this selection" without naming it: the agent
 * is told where the user is, and resolves these the way it resolves the user's own words.
 */
const WELCOMES: Record<
    AgentDockWelcomePlace,
    Omit<AgentDockWelcome, 'place' | 'title'> & { title?: string }
> = {
    selection: {
        description: 'Rewrites what you selected, or works on the rest of the page.',
        starters: [
            {
                id: 'welcome.selection.improve',
                icon: Icon.AIImproveWriting,
                label: 'Improve the selection',
                prompt: 'Review and improve this selection',
                immediate: true,
            },
            {
                id: 'welcome.selection.shorter',
                icon: Icon.AIMakeShorter,
                label: 'Make it shorter',
                prompt: 'Make this selection more concise while retaining key information',
                immediate: true,
            },
            {
                id: 'welcome.selection.typos',
                icon: Icon.AIFixSpellingGrammar,
                label: 'Fix typos and grammar',
                prompt: 'Fix typos and grammar for this selection',
                immediate: true,
            },
            {
                id: 'welcome.selection.rewrite',
                icon: Icon.Edit,
                label: 'Rewrite it as…',
                prompt: 'Rewrite this selection as ',
                immediate: false,
            },
        ],
    },
    page_in_change_request: {
        description: 'Edits this page, or anything else in the change request.',
        starters: [
            FIX_PAGE_TYPOS,
            IMPROVE_PAGE,
            {
                id: 'welcome.page.review-change-request',
                icon: Icon.ChangeRequest,
                label: 'Review the change request',
                prompt: 'Review this change request',
                immediate: true,
            },
            TRANSLATE_PAGE,
        ],
    },
    page_in_live_space: {
        description: 'Edits this page live, along with the rest of the space.',
        starters: [
            FIX_PAGE_TYPOS,
            IMPROVE_PAGE,
            {
                id: 'welcome.page.summary',
                icon: Icon.Stepper,
                label: 'Add a summary and next steps',
                prompt: 'Add a concise summary and next steps at the end of this page',
                immediate: true,
            },
            TRANSLATE_PAGE,
        ],
    },
    change_request: {
        description: 'Reads, reviews and edits the pages in this change request.',
        starters: [
            {
                id: 'welcome.change-request.review',
                icon: Icon.ChangeRequest,
                label: 'Review this change request',
                prompt: 'Review this change request',
                immediate: true,
            },
            {
                id: 'welcome.change-request.summary',
                icon: Icon.Diff,
                label: 'Summarize what changed',
                prompt: 'Summarize what changed in this change request',
                immediate: true,
            },
            {
                id: 'welcome.change-request.typos',
                icon: Icon.AIFixSpellingGrammar,
                label: 'Fix typos in the changed pages',
                prompt: 'Fix typos and grammar in the pages changed by this change request',
                immediate: true,
            },
            {
                id: 'welcome.change-request.description',
                icon: Icon.ColumnTypeText,
                label: 'Write the description',
                prompt: 'Write a title and description for this change request from its changes',
                immediate: true,
            },
        ],
    },
    space: {
        description: 'Reads this space, and opens a change request when you ask for edits.',
        starters: [
            {
                id: 'welcome.space.overview',
                icon: Icon.BookOpen,
                label: 'What does this space cover?',
                prompt: 'Give me an overview of what this space covers',
                immediate: true,
            },
            {
                id: 'welcome.space.outdated',
                icon: Icon.FindingContentOutdated,
                label: 'Find outdated content',
                prompt: 'Find outdated or inconsistent content in this space',
                immediate: true,
            },
            {
                id: 'welcome.space.typos',
                icon: Icon.AIFixSpellingGrammar,
                label: 'Fix typos across the space',
                prompt: 'Fix typos and grammar across this space',
                immediate: true,
            },
            WRITE_PAGE,
        ],
    },
    analytics: {
        title: 'Ask about this report',
        description: 'Get the story behind the numbers.',
        starters: [
            {
                id: 'welcome.analytics.takeaways',
                icon: Icon.Bulb,
                label: 'Key takeaways',
                prompt: 'Explain the key takeaways of this report',
                immediate: true,
            },
            {
                id: 'welcome.analytics.previous-period',
                icon: Icon.ChartUp,
                label: 'What changed?',
                prompt: 'Compare this report with the previous period and explain what changed',
                immediate: true,
            },
            {
                id: 'welcome.analytics.next',
                icon: Icon.Question,
                label: 'What to fix next',
                prompt: 'Based on this report, what should I look into next?',
                immediate: true,
            },
            {
                id: 'welcome.analytics.breakdown',
                icon: Icon.ScaleThreeBars,
                label: 'Break down by…',
                prompt: 'Break this report down by ',
                immediate: false,
            },
        ],
    },
    site: {
        description: 'Answers questions about this site, and starts changes to it.',
        starters: [
            {
                id: 'welcome.site.overview',
                icon: Icon.BookOpen,
                label: 'What does this site cover?',
                prompt: 'Give me an overview of what this site covers',
                immediate: true,
            },
            {
                id: 'welcome.site.gaps',
                icon: Icon.FindingContentGap,
                label: 'Find gaps in the docs',
                prompt: "Find gaps and missing topics in this site's documentation",
                immediate: true,
            },
            IMPROVE_SITE_NAVIGATION,
            WRITE_PAGE,
        ],
    },
    site_setup: {
        description: 'I can help you get this site ready to publish.',
        starters: [SITE_SETUP_GUIDE_STARTER],
    },
    organization: {
        description: 'Searches your docs, answers questions and starts changes across your sites.',
        starters: [
            {
                id: 'welcome.organization.capabilities',
                icon: Icon.HelpCircle,
                label: 'What can you help with?',
                prompt: 'What can you help me with in GitBook?',
                immediate: true,
            },
            {
                id: 'welcome.organization.open-change-requests',
                icon: Icon.ChangeRequestOpen,
                label: 'Which change requests need review?',
                prompt: 'Which change requests are open and waiting for review?',
                immediate: true,
            },
            {
                id: 'welcome.organization.find-pages',
                icon: Icon.Search,
                label: 'Find pages about…',
                prompt: 'Find the pages about ',
                immediate: false,
            },
            WRITE_PAGE,
        ],
    },
};

/**
 * What an empty conversation says and offers, for where the user is. Null while a site's
 * setup state is still loading, so the site starters do not flash before the setup ones.
 */
export function getAgentDockWelcome(context: AgentDockWelcomeContext): AgentDockWelcome | null {
    const place = getAgentDockWelcomePlace(context);
    if (!place) {
        return null;
    }
    const welcome = { place, title: DEFAULT_TITLE, ...WELCOMES[place] };
    return place === 'site_setup' && context.siteSetup === 'continue'
        ? {
              ...welcome,
              starters: [
                  {
                      ...SITE_SETUP_GUIDE_STARTER,
                      label: 'Continue the setup guide',
                      prompt: 'Continue the site setup guide',
                  },
              ],
          }
        : welcome;
}

/**
 * A starter in the shape the composer runs a template prompt in: sent, or drafted for the user to
 * finish.
 */
export function compileAgentDockStarter(starter: AgentDockStarter): AgentChatCompiledPrompt {
    return starter.immediate
        ? { id: starter.id, kind: 'submit', payload: { content: starter.prompt } }
        : { id: starter.id, kind: 'draft', payload: { content: starter.prompt } };
}

function getAgentDockWelcomePlace(context: AgentDockWelcomeContext): AgentDockWelcomePlace | null {
    const { location, page, selection, analytics, siteSetup } = context;

    if (selection) {
        return 'selection';
    }
    if (page) {
        // A page is only ever in context from a change request or a space edited live, so without
        // a change request it is the latter.
        return location.changeRequest !== undefined
            ? 'page_in_change_request'
            : 'page_in_live_space';
    }
    if (location.changeRequest !== undefined) {
        return 'change_request';
    }
    if (location.spaceId) {
        return 'space';
    }
    if (analytics) {
        return 'analytics';
    }
    if (location.siteId) {
        if (siteSetup === undefined) {
            return null;
        }
        return siteSetup ? 'site_setup' : 'site';
    }
    return 'organization';
}
